/*
 * Helper for form field validation.
 * Allows a set of validation predicates to be attached to a field (e.g. "notEmpty", or any user-defined validation predicate function).
 * Can be used to determine if any individual field is valid, as well as checking all fields in a group (e.g. a form) simultaneously.
 * 
 * This is designed to work conveniently with <Form...Field> components
 *
 * Usage: Call useValidation(). This returns an object containing two things:
 *   "areAllFieldsValid" - a ref<boolean> which indicates whether all fields associated with this validator are valid
 *   "validator" - a function which you can call, passing in a set of predicates.
 *                 This in turn returns another function, which can be called to check a particular field value against those predicates.
 *                 Calling this function also updates "areAllFieldsValid"/
 * 
 * e.g.
 * const {areAllFieldsValid, myValidator} = useValidation();  // Effectively creates a "validation group"
 * const myCustomValidation = (f:any) => f != "badValue";   // Defines a custom validation
 * 
 * // In the template
 * <FormTextField v-model="someField" :validator="myValidator(Validations.notEmpty)">
 * <FormTextField v-model="someOtherField" :validator="myValidator(Validations.notEmpty, myCustomValidation)">
 * 
 * // Check that all fields are valid
 * if (areAllFieldsValid.value) { ... }
 */
import { ref } from "vue";

// Standard validation predicates
export const Validations = {
    notEmpty : (val:any) => {
        if (val == null || val == undefined) return false;
        if (val.length == 0) return false;
        return true;
    },
    number : (val:any) => {
        return !isNaN(parseFloat(String(val)));
    },
    integer : (val:any) => {
        const asFloat = parseFloat(String(val));
        return !isNaN(asFloat) && asFloat == parseInt(String(val), 10);
    },
    email : (val:any) => {
        return String(val)
            .toLowerCase()
            .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/) != null;
    },
    phone_US : (val:any) => {
        const stripped = String(val).replace('-', '');
        return Validations.length(10)(stripped) && Validations.digits(stripped);
    },
    digits: (val:any) => {
        return String(val).match(/^[0-9]*$/) != null;
    },
    length: (len:number) => {
        return (val: any) => String(val).length == len;
    },
}

export function useValidation() {
    const invalidFields = new Set<any>();
    const areAllFieldsValid = ref<boolean>(false);

    const validator = (...predicates:((val:any)=>boolean)[]) => {
        // Calling validator() with a set of predicates creates a function, which in turn returns true if all predicates are true.
        return (fieldId:any, fieldValue:any) => {
            // Check to see if this field is valid
            let fieldIsValid = true;
            for (const predicate of predicates) {
                if (!predicate(fieldValue)) {
                    fieldIsValid = false;
                    break;
                }
            }

            // Check to see if all fields are now valid, and update tracking ref
            if (fieldIsValid) {
                invalidFields.delete(fieldId);
            }
            else {
                invalidFields.add(fieldId);
            }
            
            areAllFieldsValid.value = invalidFields.size == 0;
            
            return fieldIsValid;
        }
    };

    return {areAllFieldsValid, validator};
}